/**
 * SessionControlBar - 세션 타이머 + 연장/종료 컨트롤
 *
 * 매장 페이지 상단에 표시되는 세션 관리 바입니다.
 * - 카운트다운 타이머
 * - 연장 버튼 (횟수 표시, 최대 초과 시 비활성화)
 * - 종료 버튼 (확인 다이얼로그 포함)
 *
 * @see Issue #220, #221
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Timer, Plus, X, Loader2 } from 'lucide-react';
import { SessionTimer } from './SessionTimer';
import type { UseSessionTimerReturn } from '@/hooks/useSessionTimer';
import { useApp } from '@/contexts/AppContext';
import { clearTableSession } from '@/lib/tableContext';
import { recordSessionEnded } from '@/lib/sessionTerminalTelemetry';

interface SessionControlBarProps {
  /** useSessionTimer 훅 반환값 */
  sessionTimer: UseSessionTimerReturn;
}

/** 다국어 텍스트 */
const texts = {
  ko: {
    extend: '연장',
    end: '종료',
    confirmEnd: '세션을 종료하시겠습니까?',
    confirm: '확인',
    cancel: '취소',
    expired: '세션 만료',
    loading: '처리 중...',
  },
  en: {
    extend: 'Extend',
    end: 'End',
    confirmEnd: 'Are you sure you want to end the session?',
    confirm: 'Confirm',
    cancel: 'Cancel',
    expired: 'Session Expired',
    loading: 'Processing...',
  },
  es: {
    extend: 'Extender',
    end: 'Terminar',
    confirmEnd: '¿Desea finalizar la sesión?',
    confirm: 'Confirmar',
    cancel: 'Cancelar',
    expired: 'Sesión expirada',
    loading: 'Procesando...',
  },
} as const;

export function SessionControlBar({
  sessionTimer,
}: SessionControlBarProps) {
  const navigate = useNavigate();
  const { language } = useApp();
  const t = texts[language] || texts.en;

  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ text: string; isError: boolean } | null>(null);
  const messageTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // 메시지 타이머 정리
  useEffect(() => {
    return () => {
      if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
    };
  }, []);

  const {
    remainingSeconds,
    isWarning,
    isExpired,
    isActive,
    extendCount,
    maxExtendCount,
    canExtendMore,
    handleExtend,
    handleEnd,
    isActionLoading,
    isLoading,
    error,
  } = sessionTimer;

  // 메시지 자동 숨김 (타이머 클린업 포함)
  const showMessage = useCallback((text: string, isError: boolean) => {
    if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
    setActionMessage({ text, isError });
    messageTimerRef.current = setTimeout(() => setActionMessage(null), 3000);
  }, []);

  // 연장 처리
  const onExtendClick = useCallback(async () => {
    const result = await handleExtend();
    showMessage(result.message, !result.success);
  }, [handleExtend, showMessage]);

  // 종료 확인
  const onEndConfirm = useCallback(async () => {
    setShowEndConfirm(false);
    const result = await handleEnd();
    if (result.kind === 'manual-ended') {
      try {
        recordSessionEnded({
          eventId: crypto.randomUUID(),
          sessionId: result.sessionId,
          occurredAt: new Date().toISOString(),
        });
      } catch {
        // Telemetry must never block intentional session completion.
      }
      try {
        clearTableSession();
      } catch {
        // Storage failure must never trap the customer on an ended session.
      }
      navigate('/', { replace: true });
    } else if (result.kind === 'rejected') {
      showMessage(result.message, true);
    }
    // A terminal result is rendered by the shared coordinator mounted above.
  }, [handleEnd, navigate, showMessage]);

  // 로딩 중이면 스켈레톤 표시
  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 bg-muted/50 px-3 py-2 rounded-lg animate-pulse">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{t.loading}</span>
      </div>
    );
  }

  // 에러 상태
  if (error && !isActive) {
    return null; // 에러 시 바 자체를 숨김
  }

  // 만료 상태
  if (isExpired && !isActive) {
    return (
      <div className="flex items-center justify-between bg-destructive/10 border border-destructive/30 px-3 py-2 rounded-lg">
        <div className="flex items-center gap-2">
          <Timer className="h-4 w-4 text-destructive" />
          <span className="text-sm font-medium text-destructive">{t.expired}</span>
        </div>
        <SessionTimer
          remainingSeconds={0}
          isWarning={false}
          isExpired={true}
        />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* 메인 컨트롤 바 */}
      <div
        className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-colors ${
          isWarning
            ? 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-300 dark:border-yellow-700'
            : 'bg-background border-border'
        }`}
      >
        {/* 타이머 */}
        <SessionTimer
          remainingSeconds={remainingSeconds}
          isWarning={isWarning}
          isExpired={isExpired}
        />

        {/* 버튼 그룹 */}
        <div className="flex items-center gap-2">
          {/* 연장 버튼 */}
          <button
            onClick={onExtendClick}
            disabled={!canExtendMore || isActionLoading}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
              canExtendMore && !isActionLoading
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
          >
            {isActionLoading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Plus className="h-3 w-3" />
            )}
            <span>{t.extend}</span>
            <span className="text-[10px] opacity-70">
              ({extendCount}/{maxExtendCount})
            </span>
          </button>

          {/* 종료 버튼 */}
          <button
            onClick={() => setShowEndConfirm(true)}
            disabled={isActionLoading}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
              isActionLoading
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-destructive/10 text-destructive hover:bg-destructive/20'
            }`}
          >
            <X className="h-3 w-3" />
            <span>{t.end}</span>
          </button>
        </div>
      </div>

      {/* 액션 메시지 토스트 */}
      {actionMessage && (
        <div
          className={`absolute top-full left-0 right-0 mt-1 px-3 py-1.5 rounded-md text-xs text-center z-10 ${
            actionMessage.isError
              ? 'bg-destructive/10 text-destructive border border-destructive/30'
              : 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700'
          }`}
        >
          {actionMessage.text}
        </div>
      )}

      {/* 종료 확인 다이얼로그 */}
      {showEndConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-xl p-5 mx-4 max-w-sm w-full shadow-xl">
            <p className="text-sm text-foreground text-center mb-4">
              {t.confirmEnd}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowEndConfirm(false)}
                disabled={isActionLoading}
                className="flex-1 py-2.5 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                {t.cancel}
              </button>
              <button
                onClick={onEndConfirm}
                disabled={isActionLoading}
                className="flex-1 py-2.5 rounded-lg bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 transition-colors flex items-center justify-center gap-2"
              >
                {isActionLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {t.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
